import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import { getDatePickerEndAdornmentSx } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { parisWallClockToUtc } from "helpers/maintenanceTime";
import { useAdapterLocale } from "hooks/useAdapterLocale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateAppMaintenanceInput } from "store/adminPanelSlice";

type Props = {
    open: boolean;
    saving: boolean;
    onClose: () => void;
    onSave: (schedule: CreateAppMaintenanceInput) => void;
    onPreview: (schedule: CreateAppMaintenanceInput) => void;
};

const isValidDate = (value: Date | null): value is Date => value instanceof Date && !Number.isNaN(value.getTime());

type DateTimeFieldProps = {
    value: Date | null;
    onChange: (value: Date | null) => void;
    error?: boolean;
    helperText?: string;
};

const MaintenanceDateTimeField = ({ value, onChange, error, helperText }: DateTimeFieldProps) => (
    <DateTimePicker
        value={value}
        onChange={onChange}
        ampm={false}
        inputFormat="dd/MM/yyyy HH:mm"
        components={{
            OpenPickerIcon: () => <CalendarTodayOutlinedIcon sx={{ fontSize: 14, width: 14, height: 14 }} />,
        }}
        renderInput={(params) => (
            <StyledTextField
                {...params}
                fullWidth
                error={error}
                helperText={helperText}
                sx={getDatePickerEndAdornmentSx()}
            />
        )}
    />
);

const readSchedule = (
    startsAt: Date | null,
    endsAt: Date | null,
    title: string,
    description: string,
): CreateAppMaintenanceInput | null => {
    if (!isValidDate(startsAt) || !isValidDate(endsAt) || endsAt <= startsAt) return null;
    return {
        startsAt: parisWallClockToUtc(startsAt).toISOString(),
        endsAt: parisWallClockToUtc(endsAt).toISOString(),
        title: title.trim(),
        description: description.trim(),
    };
};

const MaintenanceFormModal = ({ open, saving, onClose, onSave, onPreview }: Props) => {
    const { t } = useTranslation();
    const adapterLocale = useAdapterLocale();
    const [startsAt, setStartsAt] = useState<Date | null>(null);
    const [endsAt, setEndsAt] = useState<Date | null>(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [dateError, setDateError] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStartsAt(null);
        setEndsAt(null);
        setTitle(t("Maintenance.Title"));
        setDescription(t("Maintenance.Body"));
        setDateError(false);
    }, [open, t]);

    const handleSave = () => {
        const schedule = readSchedule(startsAt, endsAt, title, description);
        if (!schedule) {
            setDateError(true);
            return;
        }
        setDateError(false);
        onSave(schedule);
    };

    const handlePreview = () => {
        const schedule = readSchedule(startsAt, endsAt, title, description);
        if (!schedule) {
            setDateError(true);
            return;
        }
        setDateError(false);
        onPreview(schedule);
    };

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={t("AdminPanel.MaintenanceNew")}
            style={{ width: 560, maxWidth: "calc(100vw - 32px)" }}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                <div className="maintenance-form">
                    <label>
                        {t("AdminPanel.MaintenanceStartsAt")} ({t("Maintenance.TimeZone")})
                        <MaintenanceDateTimeField value={startsAt} onChange={setStartsAt} />
                    </label>
                    <label>
                        {t("AdminPanel.MaintenanceEndsAt")} ({t("Maintenance.TimeZone")})
                        <MaintenanceDateTimeField
                            value={endsAt}
                            onChange={setEndsAt}
                            error={dateError}
                            helperText={dateError ? t("AdminPanel.MaintenanceEndAfterStart") : undefined}
                        />
                    </label>
                    <label>
                        {t("AdminPanel.MaintenanceTitle")}
                        <StyledTextField value={title} onChange={(event) => setTitle(event.target.value)} fullWidth />
                    </label>
                    <label>
                        {t("AdminPanel.MaintenanceDescription")}
                        <StyledTextField
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            sx={{
                                "&.MuiTextField-root .MuiOutlinedInput-root": {
                                    padding: "8px 16px",
                                },
                                "&.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                    padding: 0,
                                },
                            }}
                        />
                    </label>
                    <p className="maintenance-form-note">{t("AdminPanel.MaintenanceAdminsStay")}</p>
                    <StyledButton
                        type="button"
                        buttonType="secondary"
                        allowViewOnly
                        onClick={handlePreview}
                        startIcon={<SearchOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            alignSelf: "flex-start",
                            "&.MuiButton-root": { gap: "4px" },
                            "& .MuiButton-startIcon": { margin: 0 },
                        }}
                    >
                        {t("AdminPanel.MaintenancePreview")}
                    </StyledButton>
                    <div className="maintenance-form-actions">
                        <StyledButton
                            type="button"
                            buttonType="secondary"
                            allowViewOnly
                            onClick={onClose}
                            sx={{ flex: 1 }}
                        >
                            {t("AdminPanel.MaintenanceCancelForm")}
                        </StyledButton>
                        <StyledButton
                            type="button"
                            buttonType="primary"
                            allowViewOnly
                            onClick={handleSave}
                            loading={saving}
                            sx={{ flex: 1 }}
                        >
                            {t("AdminPanel.MaintenanceSave")}
                        </StyledButton>
                    </div>
                </div>
            </LocalizationProvider>
        </ModalBox>
    );
};

export default MaintenanceFormModal;
